import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import "./TeamChatModal.css";

const TeamChatModal = ({ isOpen, onClose, teamMember, currentEmployee }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteChatModal, setShowDeleteChatModal] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const pollingRef = useRef(null);

  // Get profile picture URL with fallback
  const getProfilePicture = (user, isCurrentUser = false) => {
    if (isCurrentUser) {
      return currentEmployee?.profilePicture 
        ? `${API_BASE_URL}${currentEmployee.profilePicture}`
        : null;
    }
    return teamMember?.profilePicture 
      ? `${API_BASE_URL}${teamMember.profilePicture}`
      : null;
  };

  // Get initials for avatar fallback
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(" ").map((n) => n[0]).join("").toUpperCase();
  };

  // Fetch conversation when modal opens
  useEffect(() => {
    if (isOpen && teamMember && currentEmployee) {
      fetchConversation();
      if (autoRefresh) {
        startPolling();
      }
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [isOpen, teamMember, currentEmployee, autoRefresh]);

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Smart scroll to bottom
  useEffect(() => {
    if (isNearBottom) {
      scrollToBottom();
    }
  }, [messages, isNearBottom]);

  const startPolling = () => {
    if (pollingRef.current) return;
    
    pollingRef.current = setInterval(() => {
      checkForNewMessages();
    }, 3000);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
    if (!autoRefresh) {
      startPolling();
    } else {
      stopPolling();
    }
  };

  const fetchConversation = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversation/${currentEmployee._id}/${teamMember._id}?currentUserId=${currentEmployee._id}`
      );
      
      if (response.data.success) {
        setMessages(response.data.data);
        setLastUpdate(Date.now());
        setIsNearBottom(true);
      }
    } catch (error) {
      console.error('Error fetching conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewMessages = async () => {
    if (!teamMember || !currentEmployee) return;
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/messages/conversation/${currentEmployee._id}/${teamMember._id}?currentUserId=${currentEmployee._id}`
      );
      
      if (response.data.success) {
        const newMessages = response.data.data;
        if (newMessages.length !== messages.length || 
            JSON.stringify(newMessages) !== JSON.stringify(messages)) {
          setMessages(newMessages);
          setLastUpdate(Date.now());
        }
      }
    } catch (error) {
      console.error('Error checking for new messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: "smooth",
      block: "nearest"
    });
  };

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      setIsNearBottom(distanceFromBottom <= 100);
    }
  };

  // Message handling functions
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      
      const messageData = {
        sender: currentEmployee._id,
        receiver: teamMember._id,
        message: newMessage,
        messageType: 'text'
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/messages/send`,
        messageData
      );

      if (response.data.success) {
        setMessages(prev => [...prev, response.data.data]);
        setNewMessage("");
        setLastUpdate(Date.now());
        setIsNearBottom(true);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sender', currentEmployee._id);
      formData.append('receiver', teamMember._id);

      const response = await axios.post(
        `${API_BASE_URL}/api/messages/send-file`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setMessages(prev => [...prev, response.data.data]);
        setLastUpdate(Date.now());
        setIsNearBottom(true);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Emoji handling
  const emojis = ['😀', '😂', '❤️', '👍', '👋', '🎉', '🔥', '⭐', '📁', '✅', '❌', '💡', '🎯', '✨', '💯', '👏', '🙏', '😊', '😍', '🤔', '😎', '🥳', '😢', '😡'];

  const addEmoji = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  // Single message deletion
  const handleDeleteMessage = (message) => {
    setMessageToDelete(message);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;

    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/messages/${messageToDelete._id}`
      );

      if (response.data.success) {
        setMessages(prev => prev.filter(msg => msg._id !== messageToDelete._id));
        setShowDeleteModal(false);
        setMessageToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setMessageToDelete(null);
  };

  // Delete entire chat history
  const handleDeleteChatHistory = () => {
    setShowDeleteChatModal(true);
  };

  const confirmDeleteChatHistory = async () => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/messages/conversation/${currentEmployee._id}/${teamMember._id}`
      );

      if (response.data.success) {
        setMessages([]);
        setShowDeleteChatModal(false);
        alert('Chat history deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting chat history:', error);
      alert('Failed to delete chat history. Please try again.');
    }
  };

  const cancelDeleteChatHistory = () => {
    setShowDeleteChatModal(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType, fileName) => {
    if (fileType?.startsWith('image/')) return '🖼️';
    if (fileType?.startsWith('audio/')) return '🎵';
    if (fileType?.startsWith('video/')) return '🎬';
    if (fileType?.includes('pdf')) return '📄';
    if (fileType?.includes('word')) return '📝';
    if (fileType?.includes('excel') || fileType?.includes('sheet')) return '📊';
    if (fileType?.includes('zip') || fileType?.includes('compressed')) return '📦';
    if (fileType?.includes('text')) return '📃';
    return '📎';
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement('a');
    link.href = `${API_BASE_URL}/api/messages${fileUrl}`;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  const refreshMessages = () => {
    fetchConversation();
  };

  const scrollToBottomButton = () => {
    setIsNearBottom(true);
    scrollToBottom();
  };

  const renderMessage = (message, index) => {
    const isCurrentUser = message.sender === currentEmployee._id;
    const profilePicture = isCurrentUser 
      ? getProfilePicture(currentEmployee, true)
      : getProfilePicture(teamMember, false);

    // Check if we need to show date label
    const showDateLabel = index === 0 || 
      new Date(message.timestamp).toDateString() !== new Date(messages[index - 1].timestamp).toDateString();

    return (
      <div key={message._id}>
        {/* Date Label */}
        {showDateLabel && (
          <div className="date-label">
            {formatDate(message.timestamp)}
          </div>
        )}
        
        {/* Message Container - Different classes for sent vs received */}
        <div className={`message-container ${isCurrentUser ? 'sent' : 'received'}`}>
          
          {/* Received Message (Left Side) */}
          {!isCurrentUser && (
            <div className="received-message">
              <div className="message-avatar">
                {profilePicture ? (
                  <img src={profilePicture} alt="Profile" className="message-profile-pic" />
                ) : (
                  <div className="avatar-fallback">{getInitials(teamMember?.name)}</div>
                )}
              </div>
              <div className="message-content-wrapper">
                <div className="message-bubble received-bubble">
                  {message.messageType === 'file' ? (
                    <div className="file-message">
                      <div className="file-info">
                        <span className="file-icon">{getFileIcon(message.fileType, message.fileName)}</span>
                        <div className="file-details">
                          <span className="file-name">{message.fileName}</span>
                          <span className="file-size">{formatFileSize(message.fileSize)}</span>
                        </div>
                      </div>
                      <button 
                        className="download-btn"
                        onClick={() => handleDownload(message.fileUrl, message.fileName)}
                      >
                        Download
                      </button>
                    </div>
                  ) : (
                    <div className="text-message">
                      {message.message.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="message-footer">
                  <span className="message-time">{formatTime(message.timestamp)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Sent Message (Right Side) */}
          {isCurrentUser && (
            <div className="sent-message">
              <div className="message-content-wrapper">
                <div className="message-bubble sent-bubble">
                  {message.messageType === 'file' ? (
                    <div className="file-message">
                      <div className="file-info">
                        <span className="file-icon">{getFileIcon(message.fileType, message.fileName)}</span>
                        <div className="file-details">
                          <span className="file-name">{message.fileName}</span>
                          <span className="file-size">{formatFileSize(message.fileSize)}</span>
                        </div>
                      </div>
                      <button 
                        className="download-btn"
                        onClick={() => handleDownload(message.fileUrl, message.fileName)}
                      >
                        Download
                      </button>
                    </div>
                  ) : (
                    <div className="text-message">
                      {message.message.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="message-footer">
                  <span className="message-time">{formatTime(message.timestamp)}</span>
                  <button 
                    className="delete-message-btn"
                    onClick={() => handleDeleteMessage(message)}
                    title="Delete message"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <div className="message-avatar current-user">
                {profilePicture ? (
                  <img src={profilePicture} alt="Profile" className="message-profile-pic" />
                ) : (
                  <div className="avatar-fallback">{getInitials(currentEmployee?.name)}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;

    return (
      <div className="delete-modal-overlay">
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h3>Delete Message</h3>
            <p>Are you sure you want to delete this message? This action cannot be undone.</p>
            <div className="delete-modal-actions">
              <button 
                className="cancel-delete-btn"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteChatModal = () => {
    if (!showDeleteChatModal) return null;

    return (
      <div className="delete-modal-overlay">
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h3>Delete Chat History</h3>
            <p>Are you sure you want to delete entire chat history with {teamMember?.name}? This action cannot be undone.</p>
            <div className="delete-modal-actions">
              <button 
                className="cancel-delete-btn"
                onClick={cancelDeleteChatHistory}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={confirmDeleteChatHistory}
              >
                Delete All
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmojiPicker = () => {
    if (!showEmojiPicker) return null;

    return (
      <div className="emoji-picker" ref={emojiPickerRef}>
        <div className="emoji-picker-header">
          <span>Select Emoji</span>
          <button 
            className="emoji-picker-close"
            onClick={() => setShowEmojiPicker(false)}
          >
            ×
          </button>
        </div>
        <div className="emoji-grid">
          {emojis.map((emoji, index) => (
            <button
              key={index}
              className="emoji-btn"
              onClick={() => addEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const currentUserProfilePicture = getProfilePicture(currentEmployee, true);
  const teamMemberProfilePicture = getProfilePicture(teamMember, false);

  return (
    <div className="team-chat-modal-overlay">
      <div className="team-chat-modal">
        {/* Header */}
        <div className="team-chat-header">
          <div className="team-chat-user-info">
            {teamMemberProfilePicture ? (
              <img src={teamMemberProfilePicture} alt="Profile" className="team-chat-profile-pic" />
            ) : (
              <div className="team-chat-avatar">
                {getInitials(teamMember?.name)}
              </div>
            )}
            <div>
              <h3 className="team-chat-user-name">{teamMember?.name}</h3>
              <p className="team-chat-user-status">Online</p>
            </div>
          </div>
          
          <div className="team-chat-header-actions">
            <div className="refresh-controls">
              <button 
                className="refresh-btn" 
                onClick={refreshMessages} 
                disabled={loading}
                title="Refresh messages"
              >
                {loading ? '🔄' : '↻'}
              </button>
              <button 
                className={`auto-refresh-btn ${autoRefresh ? 'active' : ''}`} 
                onClick={toggleAutoRefresh}
                title={autoRefresh ? 'Auto-refresh on' : 'Auto-refresh off'}
              >
                {autoRefresh ? '⏰' : '⏸️'}
              </button>
              <button 
                className="delete-chat-btn"
                onClick={handleDeleteChatHistory}
                title="Delete chat history"
              >
                🗑️
              </button>
            </div>
            
            <button className="team-chat-close" onClick={onClose}>×</button>
          </div>
        </div>

        {/* Messages Container */}
        <div 
          className="team-chat-messages" 
          ref={messagesContainerRef} 
          onScroll={handleScroll}
        >
          {loading ? (
            <div className="loading-messages">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="no-messages">
              <div className="no-messages-icon">💬</div>
              <p>No messages yet</p>
              <p className="no-messages-subtitle">Start a conversation with {teamMember?.name}</p>
            </div>
          ) : (
            <>
              {messages.map((message, index) => renderMessage(message, index))}
              <div ref={messagesEndRef} />
            </>
          )}
          
          {!isNearBottom && (
            <button 
              className="scroll-to-bottom-btn"
              onClick={scrollToBottomButton}
            >
              ↓
            </button>
          )}
        </div>

        {/* Input Area */}
        <div className="team-chat-input-area">
          <div className="chat-actions">
            <button 
              className="emoji-picker-btn"
              onClick={toggleEmojiPicker}
              title="Add emoji"
            >
              😊
            </button>
            
            <button 
              className="file-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Send file"
            >
              {uploading ? (
                <div className="upload-spinner"></div>
              ) : (
                '📎'
              )}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              accept="*/*"
            />
          </div>

          <form className="team-chat-input-form" onSubmit={handleSendMessage}>
            <div className="team-chat-input-container">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="team-chat-input"
                disabled={sending}
              />
              <button 
                type="submit" 
                className="team-chat-send-button"
                disabled={sending || !newMessage.trim()}
              >
                {sending ? (
                  <div className="team-chat-send-spinner"></div>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          </form>

          {/* Emoji Picker */}
          {renderEmojiPicker()}
        </div>

        {/* Delete Confirmation Modal */}
        {renderDeleteModal()}
        
        {/* Delete Chat History Modal */}
        {renderDeleteChatModal()}
      </div>
    </div>
  );
};

export default TeamChatModal;